<script setup lang="ts">
import { apiUser } from '@api/user';
import { BaseButton, BaseCheck, BaseInput, BasePassword } from '@components/base';
import { AUTH_ROUTES } from '@constants/auth-routes';
import { getAuthApiErrorMessage } from '@helpers/auth-api-error';
import { getPostLoginRedirectUrl } from '@helpers/user-menu';
import { passwordRules } from '@helpers/validators';
import { UserRegisterPayload } from '@models/user';
import { useUserStore } from '@stores/index';
import { useVuelidate } from '@vuelidate/core';
import { email, maxLength, minLength, required } from '@vuelidate/validators';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { useToast } from 'vue-toast-notification';

const props = withDefaults(
  defineProps<{
    content?: Record<string, string>;
  }>(),
  { content: () => ({}) },
);

const toast = useToast();
const userStore = useUserStore();
const siteHandle = typeof window !== 'undefined' ? window.siteHandle : 'jrnys';
const skipEmailVerification = typeof window !== 'undefined' && Boolean(window.skipEmailVerification);

type SignUpStep = 'details' | 'verify';

const step = ref<SignUpStep>('details');
const sendingCode = ref(false);
const resendCooldown = ref(0);
let resendTimer: ReturnType<typeof setInterval> | null = null;

const formData = reactive<UserRegisterPayload & { verificationCode: string }>({
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  siteHandle,
  verificationCode: '',
});

const agreeToTerms = ref(false);
const marketingEmails = ref(false);

const detailsRules = {
  firstName: { required },
  email: { email, required },
  password: passwordRules,
};

const verifyRules = {
  verificationCode: {
    required,
    minLength: minLength(6),
    maxLength: maxLength(6),
  },
};

const detailsV$ = useVuelidate(detailsRules, formData);
const verifyV$ = useVuelidate(verifyRules, formData, { $scope: false });

const title = computed(() => props.content?.title ?? 'Welcome to JRNYS');
const createAccountText = computed(() => props.content?.createAccount ?? 'CREATE ACCOUNT');
const logInText = computed(() => props.content?.logIn ?? 'Log in here');
const nameError = computed(() => detailsV$.value.firstName.$errors[0]?.$message ?? '');
const emailError = computed(() => detailsV$.value.email.$errors[0]?.$message ?? '');
const passwordError = computed(() => detailsV$.value.password.$errors[0]?.$message ?? '');
const otpError = computed(() => verifyV$.value.verificationCode.$errors[0]?.$message ?? '');

const maskedEmail = computed(() => {
  const value = formData.email;
  const at = value.indexOf('@');
  if (at <= 1) {
    return value;
  }

  return `${value.slice(0, 2)}***${value.slice(at)}`;
});

const startResendCooldown = (seconds = 60) => {
  resendCooldown.value = seconds;
  if (resendTimer) {
    clearInterval(resendTimer);
  }
  resendTimer = setInterval(() => {
    if (resendCooldown.value <= 1) {
      resendCooldown.value = 0;
      if (resendTimer) {
        clearInterval(resendTimer);
        resendTimer = null;
      }
      return;
    }
    resendCooldown.value -= 1;
  }, 1000);
};

const sendVerificationCode = async () => {
  const isValid = await detailsV$.value.$validate();
  if (!isValid || !agreeToTerms.value) {
    return;
  }

  try {
    sendingCode.value = true;
    await apiUser.sendEmailVerification(
      formData.email.trim(),
      formData.siteHandle,
      formData.firstName.trim(),
    );
    formData.verificationCode = '';
    verifyV$.value.$reset();
    step.value = 'verify';
    toast.success('Check your email for a 6-character verification code.');
    startResendCooldown();
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : '';
    toast.error(getAuthApiErrorMessage(message));
  } finally {
    sendingCode.value = false;
  }
};

const resendVerificationCode = async () => {
  if (resendCooldown.value > 0) {
    return;
  }
  await sendVerificationCode();
};

const backToDetails = () => {
  step.value = 'details';
  formData.verificationCode = '';
  verifyV$.value.$reset();
};

const completeRegistration = async () => {
  if (!skipEmailVerification) {
    const otpValid = await verifyV$.value.$validate();
    if (!otpValid) {
      return;
    }
  } else {
    const isValid = await detailsV$.value.$validate();
    if (!isValid || !agreeToTerms.value) {
      return;
    }
  }

  formData.siteHandle = siteHandle;

  const result = await userStore.register({
    email: formData.email.trim(),
    password: formData.password,
    firstName: formData.firstName.trim(),
    lastName: '',
    siteHandle,
    verificationCode: skipEmailVerification
      ? undefined
      : formData.verificationCode.trim().toUpperCase(),
    consent: {
      consentEmailGeneralMarketing: marketingEmails.value,
      consentEmailPersonalizedQuiz: marketingEmails.value,
    },
  });

  if (!result) {
    return;
  }

  window.location.assign(getPostLoginRedirectUrl(userStore.info));
};

const onDetailsSubmit = () => {
  if (skipEmailVerification) {
    completeRegistration();
    return;
  }
  sendVerificationCode();
};

onUnmounted(() => {
  if (resendTimer) {
    clearInterval(resendTimer);
  }
});
</script>

<template>
  <form
    class="flex w-full flex-col gap-24"
    @submit.prevent="step === 'verify' ? completeRegistration() : onDetailsSubmit()"
  >
    <h1 class="font-inter-tight text-20 font-semibold text-off-black-500 lg:text-26">
      {{ title }}
    </h1>

    <template v-if="step === 'details'">
      <div class="flex flex-col gap-16">
        <BaseInput
          :model-value="formData.firstName"
          type="text"
          placeholder="Name"
          autocomplete="name"
          :error="detailsV$.firstName.$error"
          :error-message="(nameError as unknown) as string"
          @update:model-value="formData.firstName = $event.trim()"
        />
        <BaseInput
          :model-value="formData.email"
          type="email"
          placeholder="Email"
          autocomplete="email"
          :error="detailsV$.email.$error"
          :error-message="(emailError as unknown) as string"
          @update:model-value="formData.email = $event.trim()"
        />
        <BasePassword
          v-model="formData.password"
          placeholder="Password"
          autocomplete="new-password"
          :error="detailsV$.password.$error"
          :error-message="(passwordError as unknown) as string"
        />
      </div>

      <div class="flex flex-col gap-20">
        <BaseCheck v-model="agreeToTerms" variant="auth">
          By creating an account, I agree to the
          <a
            href="/terms-and-conditions"
            class="underline hover:no-underline"
            target="_blank"
            rel="noopener noreferrer"
          >Terms & Conditions</a>
          and acknowledge the
          <a
            href="/privacy-policy"
            class="underline hover:no-underline"
            target="_blank"
            rel="noopener noreferrer"
          >Privacy
            Policy</a>.
        </BaseCheck>
        <BaseCheck v-model="marketingEmails" variant="auth">
          Yes, I'd like to receive personalized marketing emails with updates, resources, and offers. This is separate
          from required medical communications.
        </BaseCheck>
      </div>

      <BaseButton
        :text="skipEmailVerification ? createAccountText : 'SEND VERIFICATION CODE'"
        type="submit"
        variant="primary"
        class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
        :loading="sendingCode || userStore.loading"
        :disabled="detailsV$.$error || !agreeToTerms"
      >
        <template #icon-right>
          <span
            class="size-20 opacity-0"
            aria-hidden="true"
          />
        </template>
      </BaseButton>
    </template>

    <template v-else>
      <p class="text-14 text-off-black-500">
        Enter the 6-character code we sent to
        <span class="font-medium">{{ maskedEmail }}</span>.
        Codes expire after 15 minutes.
      </p>

      <BaseInput
        :model-value="formData.verificationCode"
        type="text"
        placeholder="Verification code"
        autocomplete="one-time-code"
        maxlength="6"
        :error="verifyV$.verificationCode.$error"
        :error-message="(otpError as unknown) as string"
        @update:model-value="formData.verificationCode = $event.trim().toUpperCase()"
      />

      <div class="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          class="text-14 font-medium text-teal-500 hover:underline"
          @click="backToDetails"
        >
          Change email
        </button>
        <button
          type="button"
          class="text-14 text-off-black-500 disabled:opacity-50"
          :disabled="resendCooldown > 0 || sendingCode"
          @click="resendVerificationCode"
        >
          {{ resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code' }}
        </button>
      </div>

      <BaseButton
        :text="createAccountText"
        type="submit"
        variant="primary"
        class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
        :loading="userStore.loading"
        :disabled="verifyV$.$error"
      >
        <template #icon-right>
          <span
            class="size-20 opacity-0"
            aria-hidden="true"
          />
        </template>
      </BaseButton>
    </template>

    <p class="text-center text-14 text-off-black-500">
      Do you have an account?
      <a
        :href="AUTH_ROUTES.LOGIN"
        class="font-medium text-teal-500 hover:underline"
      >
        {{ logInText }}
      </a>
    </p>
  </form>
</template>
